import React, { useMemo } from "react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { computeQcVerdict, type QcDefectCounts } from "@/lib/aql";
import { describePendingCriteria, pendingRequiredCriteria } from "./checklist";

export type QcFinalStatus = "PASSED" | "FAILED" | "WARNING";

interface VerdictBarProps {
  counts: QcDefectCounts;
  sampleSize: number;
  reMajor: number | null;
  reMinor: number | null;
  criteria: Array<{ result?: string | null }>;
  /** Nhân nhượng (ghi đè Không đạt) */
  override: boolean;
  onOverrideChange?: (v: boolean) => void;
  overrideReason: string;
  onOverrideReasonChange?: (v: string) => void;
  reasonError?: string | null;
  readOnly?: boolean;
  idPrefix: string;
  /** Ô thêm trong khối kết luận (SL nhập kho, SL loại, nhận xét...) */
  children?: React.ReactNode;
}

const VERDICT_STYLE: Record<QcFinalStatus, { label: string; cls: string }> = {
  PASSED: { label: "Đạt", cls: "border-emerald-400 bg-emerald-50 text-emerald-800" },
  FAILED: { label: "Không đạt", cls: "border-red-400 bg-red-50 text-red-800" },
  WARNING: { label: "Nhân nhượng", cls: "border-amber-400 bg-amber-50 text-amber-900" },
};

/** Trạng thái lưu từ kết luận tự tính + lựa chọn Nhân nhượng */
export function resolveFormStatus(computed: "PASSED" | "FAILED", override: boolean): QcFinalStatus {
  return override && computed === "FAILED" ? "WARNING" : computed;
}

/**
 * Kết luận: số lỗi theo mức so với Re → Đạt / Không đạt tự động.
 * Người kiểm chỉ ghi đè được bằng "Nhân nhượng" khi kết quả Không đạt, và phải ghi lý do.
 */
export function VerdictBar({
  counts,
  sampleSize,
  reMajor,
  reMinor,
  criteria,
  override,
  onOverrideChange,
  overrideReason,
  onOverrideReasonChange,
  reasonError,
  readOnly,
  idPrefix,
  children,
}: VerdictBarProps) {
  const v = useMemo(() => computeQcVerdict({ counts, reMajor, reMinor, criteria }), [counts, reMajor, reMinor, criteria]);
  const status = resolveFormStatus(v.verdict, override);
  const style = VERDICT_STYLE[status];
  const ng = counts.critical + counts.major + counts.minor;
  const ok = Math.max(0, (sampleSize || 0) - ng);
  const canOverride = v.verdict === "FAILED" && !readOnly && Boolean(onOverrideChange);

  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-3.5 shadow-2xs" data-testid="qc-verdict-bar">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-800">Kết luận</div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-700" data-testid="qc-verdict-counts">
            <span>
              Nghiêm trọng <b className={cn("font-mono", counts.critical > 0 && "text-red-700")}>{counts.critical}</b>
            </span>
            <span>
              Nặng <b className="font-mono">{counts.major}</b>
              {reMajor ? <span className="text-slate-500">/Re {reMajor}</span> : null}
            </span>
            <span>
              Nhẹ <b className="font-mono">{counts.minor}</b>
              {reMinor ? <span className="text-slate-500">/Re {reMinor}</span> : <span className="text-slate-500"> (không xét)</span>}
            </span>
            <span>
              Mẫu đạt <b className="font-mono">{ok}</b>/{sampleSize || 0}
            </span>
            {v.failedCriteria > 0 && (
              <span className="text-red-700">
                Chỉ tiêu không đạt <b className="font-mono">{v.failedCriteria}</b>
              </span>
            )}
            {v.pendingCriteria > 0 && (
              <span className="text-amber-700">
                Chưa kiểm <b className="font-mono">{v.pendingCriteria}</b> chỉ tiêu
              </span>
            )}
          </div>
          {v.reasons.length > 0 && <div className="text-[11px] text-red-700">{v.reasons.join(" · ")}</div>}
        </div>
        <div className="flex items-center gap-3">
          <div
            className={cn("rounded-lg border-2 px-4 py-1.5 text-sm font-black", style.cls)}
            data-testid="qc-verdict"
            data-status={status}
          >
            {style.label}
          </div>
          {onOverrideChange && (
            <label
              className={cn(
                "flex items-center gap-1.5 text-xs font-semibold",
                canOverride ? "cursor-pointer text-amber-900" : "cursor-not-allowed text-slate-400",
              )}
              title={canOverride ? "" : "Chỉ dùng khi kết luận Không đạt"}
            >
              <input
                type="checkbox"
                id={`${idPrefix}-override`}
                checked={override && v.verdict === "FAILED"}
                disabled={!canOverride}
                onChange={(e) => onOverrideChange(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 accent-amber-600"
              />
              Nhân nhượng
            </label>
          )}
        </div>
      </div>

      {status === "WARNING" && (
        <div className="space-y-1">
          <Label htmlFor={`${idPrefix}-override-reason`} className="text-xs font-semibold text-amber-900">
            Lý do nhân nhượng <span className="text-destructive">*</span>
          </Label>
          <Textarea
            id={`${idPrefix}-override-reason`}
            rows={2}
            readOnly={readOnly || !onOverrideReasonChange}
            value={overrideReason}
            onChange={(e) => onOverrideReasonChange?.(e.target.value)}
            placeholder="VD: Khách hàng đồng ý nhận, lỗi không ảnh hưởng sử dụng"
            className={cn("text-xs", reasonError && "border-destructive")}
          />
          {reasonError ? <p className="text-[11px] font-medium text-destructive">{reasonError}</p> : null}
        </div>
      )}

      {children}
    </div>
  );
}

const STORED_STATUS: Record<string, { label: string; cls: string }> = {
  ...VERDICT_STYLE,
  PENDING: { label: "Chờ kiểm", cls: "border-slate-300 bg-slate-50 text-slate-700" },
  INSPECTING: { label: "Đang kiểm", cls: "border-sky-300 bg-sky-50 text-sky-800" },
  REWORK: { label: "Làm lại", cls: "border-orange-300 bg-orange-50 text-orange-800" },
  RE_INSPECT: { label: "Kiểm lại", cls: "border-violet-300 bg-violet-50 text-violet-800" },
};

/** Khối kết luận chỉ đọc (trang xem phiếu): trạng thái đã lưu + số lỗi so với Re + lý do nhân nhượng */
export function VerdictSummary({
  status,
  counts,
  sampleSize,
  reMajor,
  reMinor,
  overrideReason,
  criteria,
  children,
}: {
  status?: string | null;
  counts: QcDefectCounts;
  sampleSize?: number | null;
  reMajor?: number | null;
  reMinor?: number | null;
  overrideReason?: string | null;
  /** Chỉ tiêu của biên bản — còn chỉ tiêu bắt buộc chưa chấm thì giải thích vì sao vẫn Đang kiểm */
  criteria?: any[];
  children?: React.ReactNode;
}) {
  const pending = String(status || "").toUpperCase() === "INSPECTING" ? pendingRequiredCriteria(criteria || []) : [];
  const st = STORED_STATUS[String(status || "").toUpperCase()] || { label: status || "—", cls: "border-slate-300 bg-white" };
  const ng = counts.critical + counts.major + counts.minor;
  return (
    <div className="space-y-2 rounded-xl border border-slate-200 bg-white p-3.5 shadow-2xs" data-testid="qc-verdict-bar">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-1">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-800">Kết luận</div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-700">
            <span>
              Nghiêm trọng <b className="font-mono">{counts.critical}</b>
            </span>
            <span>
              Nặng <b className="font-mono">{counts.major}</b>
              {reMajor ? <span className="text-slate-500">/Re {reMajor}</span> : null}
            </span>
            <span>
              Nhẹ <b className="font-mono">{counts.minor}</b>
              {reMinor ? <span className="text-slate-500">/Re {reMinor}</span> : null}
            </span>
            {sampleSize ? (
              <span>
                Mẫu đạt <b className="font-mono">{Math.max(0, sampleSize - ng)}</b>/{sampleSize}
              </span>
            ) : null}
          </div>
        </div>
        <div className={cn("rounded-lg border-2 px-4 py-1.5 text-sm font-black", st.cls)} data-testid="qc-verdict">
          {st.label}
        </div>
      </div>
      {pending.length > 0 ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900" data-testid="qc-pending-criteria">
          <b>Chưa kết luận Đạt:</b> còn {pending.length} chỉ tiêu bắt buộc chưa chấm — {describePendingCriteria(pending)}. Bấm [Sửa], nhập rồi lưu lại.
        </div>
      ) : null}
      {String(status).toUpperCase() === "WARNING" && overrideReason ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900">
          <b>Lý do nhân nhượng:</b> {overrideReason}
        </div>
      ) : null}
      {children}
    </div>
  );
}
